import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import ConsoleErrorHandler from './components/ConsoleErrorHandler'
import ApiStatusNotification from './components/ApiStatusNotification'
import SessionManager from './components/SessionManager'
import { LoginPage } from './features/auth/LoginPage'
import { RegisterPage } from './features/auth/RegisterPage'
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './features/auth/ResetPasswordPage'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { SuperAdminDashboard } from './features/admin/SuperAdminDashboard'
import { FirmAdminDashboard } from './features/firmAdmin/FirmAdminDashboard'
import { SubscriptionPage } from './features/firmAdmin/SubscriptionPage'
import { StripeCallback } from './features/firmAdmin/StripeCallback'
import { ClientManagement } from './features/firmAdmin/ClientManagement'
import { BillingInvoicing } from './features/firmAdmin/BillingInvoicing'

import { ReportsAnalytics } from './features/firmAdmin/ReportsAnalytics'
import { TaskManagement } from './features/firmAdmin/TaskManagement'
import { StaffTaskDashboard } from './features/dashboard/StaffTaskDashboard'
import { ManagerTaskDashboard } from './features/dashboard/ManagerTaskDashboard'
import { UserDashboard } from './features/user/UserDashboard'
import UserDashboardSimple from './features/user/UserDashboardSimple'
import { UserSettings } from './features/user/UserSettings'
import { UserTasks } from './features/user/UserTasks'
import { UserTimesheet } from './features/user/UserTimesheet'
import { UserTeamManagement } from './features/user/UserTeamManagement'
import { UserReports } from './features/user/UserReports'
import { UserProfile } from './features/user/UserProfile'
import UserAttendance from './features/user/UserAttendance'
import ManagerLeaveApproval from './features/user/ManagerLeaveApproval'
import { ClientDashboard, ClientTimeTracking, ClientReports } from './features/client'
import { PlatformDashboard } from './features/platform/PlatformDashboard'
import { TenantManagement } from './features/platform/TenantManagement'
import { TenantDetail } from './features/platform/TenantDetail'
import { TenantForm } from './features/platform/TenantForm'
import { TenantSettings } from './features/platform/TenantSettings'
import PlanList from './features/platform/plans/PlanList'
import PlanForm from './features/platform/plans/PlanForm'
import TenantPlanAssignment from './features/platform/plans/TenantPlanAssignment'
import PlanSelection from './features/tenant/plans/PlanSelection'
import CurrentPlan from './features/tenant/plans/CurrentPlan'
import CheckoutSuccess from './features/tenant/plans/CheckoutSuccess'
import CheckoutCancel from './features/tenant/plans/CheckoutCancel'
import { UserManagement } from './features/firmAdmin/UserManagement'
import { FirmSettings } from './features/firmAdmin/FirmSettings'
import FirmAdminLeaveApproval from './features/firmAdmin/FirmAdminLeaveApproval'
import { NotificationTemplateManagement } from './features/firmAdmin/NotificationTemplateManagement'
import TenantNotificationConfigManagement from './features/firmAdmin/NotificationConfigManagement'
import PlatformNotificationConfigManagement from './features/platform/NotificationConfigManagement'
import { CountryWorkflows } from './features/firmAdmin/CountryWorkflows'
import { FirmAdminLayout } from './features/firmAdmin/FirmAdminLayout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useAuthStore } from './features/auth/authStore'
import { TimerProvider } from './contexts/TimerContext'
import { TestAdminAccess } from './TestAdminAccess'
import { DebugAuth } from './DebugAuth'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Temporary landing page component
const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-blue via-white to-pastel-green flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-soft-lg p-12 border border-white/20">
          <div className="mb-8">
            <h1 className="text-6xl font-display font-bold text-primary-500 mb-4">
              ERPCA
            </h1>
            <p className="text-xl text-gray-600 font-body">
              Enterprise Resource Planning for Canadian Accountants
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white/60 rounded-2xl p-6 shadow-neu">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Task Management</h3>
              <p className="text-gray-600 text-sm">Streamlined workflow from Preparer → Reviewer → Submitter</p>
            </div>
            
            <div className="bg-white/60 rounded-2xl p-6 shadow-neu">
              <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Time & Attendance</h3>
              <p className="text-gray-600 text-sm">Comprehensive tracking with biometric import support</p>
            </div>
            
            <div className="bg-white/60 rounded-2xl p-6 shadow-neu">
              <div className="w-12 h-12 bg-pastel-purple rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Document Management</h3>
              <p className="text-gray-600 text-sm">Secure storage with built-in e-signature capabilities</p>
            </div>
            
            <div className="bg-white/60 rounded-2xl p-6 shadow-neu">
              <div className="w-12 h-12 bg-pastel-orange rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Canadian Compliance</h3>
              <p className="text-gray-600 text-sm">Built for T2, GST/HST, EI/CPP, and Canadian tax requirements</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/register"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-primary-500 hover:bg-primary-600 transition-colors shadow-soft"
              >
                Start Free Trial
              </a>
              <a
                href="/login"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-soft"
              >
                Sign In
              </a>
            </div>

            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <div className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></div>
                Backend API: Ready
              </span>
              <span className="flex items-center">
                <div className="w-2 h-2 bg-secondary-500 rounded-full mr-2"></div>
                Frontend: Ready
              </span>
            </div>

            <p className="text-sm text-gray-500">
              🚀 Development server running • Multi-firm architecture • Marshmello design system
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Root redirect component
const RootRedirect: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  // Show loading while auth is being initialized
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Navigate based on user role
  if (user.isPlatformUser) {
    return <Navigate to="/platform" replace />;
  } else if (user.roles?.includes('FIRM_ADMIN') || user.roles?.includes('FirmAdmin')) {
    return <Navigate to="/firm-admin" replace />;
  } else if (user.roles?.includes('Manager') || user.roles?.includes('Staff')) {
    return <Navigate to="/user-dashboard" replace />;
  } else if (user.roles?.includes('Client')) {
    return <Navigate to="/client/dashboard" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

// Wrapper component to provide timer context with user ID
const AppWithTimer: React.FC = () => {
  const { user } = useAuthStore();

  return (
    <TimerProvider userId={user?.id || null}>
      <div className="App">
        <SessionManager />
        <Routes>
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/landing" element={<LandingPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Platform Admin routes */}
            <Route
              path="/platform"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN']}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Tenant Management routes */}
            <Route
              path="/platform/tenants"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN', 'SUPPORT_ADMIN', 'BILLING_ADMIN']}>
                  <TenantManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/tenants/new"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN']}>
                  <TenantForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/tenants/:id"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN', 'SUPPORT_ADMIN', 'BILLING_ADMIN']}>
                  <TenantDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/tenants/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN']}>
                  <TenantForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/tenants/:id/settings"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN']}>
                  <TenantSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/notification-config"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'PLATFORM_ADMIN']}>
                  <PlatformNotificationConfigManagement />
                </ProtectedRoute>
              }
            />

            {/* Plan Management routes */}
            <Route
              path="/platform/plans"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
                  <PlanList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/plans/new"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
                  <PlanForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/plans/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
                  <PlanForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/platform/plans/assign"
              element={
                <ProtectedRoute requiredRoles={['SUPER_ADMIN']}>
                  <TenantPlanAssignment />
                </ProtectedRoute>
              }
            />

            {/* SuperAdmin routes (legacy) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRoles={['SuperAdmin']}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* FirmAdmin routes */}
            {/* Public Stripe callback route - must be BEFORE protected routes */}
            <Route
              path="/stripe/callback"
              element={<StripeCallback />}
            />
            
            <Route
              path="/firm-admin"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <FirmAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/subscription"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/users"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/clients"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <ClientManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/billing"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <BillingInvoicing />
                </ProtectedRoute>
              }
            />

            <Route
              path="/firm-admin/reports"
              element={
                <ProtectedRoute>
                  <ReportsAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/workflows"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <FirmAdminLayout>
                    <CountryWorkflows />
                  </FirmAdminLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/tasks"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <TaskManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/leave-approval"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <FirmAdminLeaveApproval />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/email-templates"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <NotificationTemplateManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/notification-config"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <TenantNotificationConfigManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/firm-admin/settings"
              element={
                <ProtectedRoute requiredRoles={['FirmAdmin', 'FIRM_ADMIN']}>
                  <FirmSettings />
                </ProtectedRoute>
              }
            />

            {/* User Dashboard routes for Staff and Manager */}
            <Route
              path="/user-dashboard"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard-simple"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserDashboardSimple />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/settings"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/tasks"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/timesheet"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserTimesheet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/attendance"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserAttendance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/team"
              element={
                <ProtectedRoute requiredRoles={['Manager', 'MANAGER']}>
                  <UserTeamManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/reports"
              element={
                <ProtectedRoute requiredRoles={['Manager', 'MANAGER']}>
                  <UserReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/profile"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF', 'Manager', 'MANAGER']}>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-dashboard/leave-approval"
              element={
                <ProtectedRoute requiredRoles={['Manager', 'MANAGER', 'FirmAdmin', 'FIRM_ADMIN']}>
                  <ManagerLeaveApproval />
                </ProtectedRoute>
              }
            />

            {/* Client Dashboard routes */}
            <Route
              path="/client/dashboard"
              element={
                <ProtectedRoute requiredRoles={['Client', 'CLIENT']}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/time-tracking"
              element={
                <ProtectedRoute requiredRoles={['Client', 'CLIENT']}>
                  <ClientTimeTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/reports"
              element={
                <ProtectedRoute requiredRoles={['Client', 'CLIENT']}>
                  <ClientReports />
                </ProtectedRoute>
              }
            />

            {/* Tenant Plan routes */}
            <Route
              path="/plans"
              element={
                <ProtectedRoute>
                  <PlanSelection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans/current"
              element={
                <ProtectedRoute requiredRoles={['FIRM_ADMIN', 'FirmAdmin']}>
                  <CurrentPlan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans/cancel"
              element={
                <ProtectedRoute>
                  <CheckoutCancel />
                </ProtectedRoute>
              }
            />

            {/* Role-based Task Dashboard routes */}
            <Route
              path="/tasks/staff"
              element={
                <ProtectedRoute requiredRoles={['Staff', 'STAFF']}>
                  <StaffTaskDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/manager"
              element={
                <ProtectedRoute requiredRoles={['Manager', 'MANAGER', 'FirmAdmin', 'FIRM_ADMIN']}>
                  <ManagerTaskDashboard />
                </ProtectedRoute>
              }
            />

            {/* Test route - no protection */}
            <Route path="/test-admin" element={<TestAdminAccess />} />

            {/* Debug auth route */}
            <Route path="/debug-auth" element={<DebugAuth />} />

            {/* Direct admin access - bypass protection for testing */}
            <Route path="/admin-direct" element={<SuperAdminDashboard />} />

            {/* Catch all - redirect to root */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#374151',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
            }}
          />
          <ApiStatusNotification />
        </div>
      </TimerProvider>
    );
  };

function App() {
  return (
    <ConsoleErrorHandler>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppWithTimer />
        </Router>
      </QueryClientProvider>
    </ConsoleErrorHandler>
  );
}

export default App
